import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SingingPreferencesForm } from "@/components/SingingPreferencesForm";
import { Sparkles } from "lucide-react";
import logo from "@/assets/logo.svg";

const Onboarding = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      // Check if user has pending coach application
      const { data: application } = await supabase
        .from("tutor_applications")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (application) {
        // Coach applicants skip student onboarding
        navigate("/");
        return;
      }

      // Check if preferences already completed
      const { data: profile } = await supabase
        .from("profiles")
        .select("preferences_completed")
        .eq("id", user.id)
        .single();

      if (profile?.preferences_completed) {
        navigate("/");
        return;
      }

      setUserId(user.id);
    } catch (error) {
      console.error("Error checking user:", error);
      navigate("/auth");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 py-12 px-4">
      <div className="container mx-auto max-w-3xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Vocalzing" className="h-16" />
          </div>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">Welcome to Vocalzing!</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Let's Personalize Your Journey
          </h1>
          <p className="text-xl text-muted-foreground">
            Help us customize your vocal training experience
          </p>
        </div>

        {/* Form */}
        {userId && (
          <SingingPreferencesForm
            userId={userId}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
};

export default Onboarding;
